/**
 * The one dog pose driver, and it is the third dog that paid for it.
 *
 * `src/world/corridorDirector.ts` (beat 4) and `src/world/outDogs.ts` (beat 7)
 * each carried their own copy of this arithmetic. The duplicate was deliberate
 * and it was filed the day it was made (`docs/backlog.md`: *"extracting a shared
 * dog-animation module would have meant editing beat 4's shipped director in the
 * same session that shipped beat 7, which is a worse trade than one explicit
 * duplicate — the third dog is where the extraction pays for itself"*). Beat 10's
 * night dog is the third, and a third copy is where a duplicate stops being a
 * decision and becomes a defect.
 *
 * **The two copies did not agree, and the differences are all preserved here as
 * inputs rather than reconciled away.** Beat 4's dog has four poses and never
 * moves its tail; beat 7's has four different ones and sways it. Neither is
 * wrong: a dog at a handler's heel indoors and a dog quartering a wet field are
 * not the same animal. So this module takes *what the animal is doing* — three
 * booleans and a clock — and not a pose name, because a pose vocabulary is
 * exactly the thing the two beats disagreed about.
 *
 * **This is presentation.** It reads a clock the render step owns and writes
 * `THREE.Object3D` rotations, and it must never be given a value the fixed step
 * reads back (D-045). Its caller clamps the frame dt; this function has no state
 * at all.
 */

import type { DogParts } from '@/models/dog';

/**
 * How far the nose goes down, how fast it sweeps, and how wide.
 *
 * These are beat 4's numbers, verbatim, because beat 4's is the copy that has
 * been looked at on screen — `docs/backlog.md` says so in as many words and it
 * is the whole reason this file is not beat 7's version. Beat 7's constants were
 * identical anyway; the two copies differed only in which pose set each mapped
 * onto them.
 *
 * ── Why `neckDown` is what it is ────────────────────────────────────────────
 *
 * Carried from `corridorDirector.ts`, where it was derived when stage 5 first
 * rendered a dog that was supposed to be reading a floor and was standing at
 * attention instead. The neck's rest angle already carries the head well above
 * the topline (`dog.ts`'s NECK_ROOT / NECK_TOP, and that file records getting it
 * wrong the other way), so a modest droop only brings the muzzle back to level.
 *
 * **The nose cannot reach the ground and turning this number up does not fix
 * that.** The nose sits 0.264 m from the neck root and the root is 0.418 m up,
 * so no rotation of that one pivot touches the floor; past about 2.1 rad the
 * muzzle points backwards and the animal looks broken. The floor of what this
 * rig can do is about 0.23 m off the concrete, and closing it would need a
 * second pivot in the asset.
 *
 * **The prose that came with this constant derived 1.32 and the constant has
 * been 1.15 since the commit that introduced it** (`a77a2b4`). The value is what
 * has been on screen, so the value is what moved here; the discrepancy is
 * recorded rather than quietly resolved in either direction.
 *
 * The sweep is on the **neck** and not the head. Once the neck is pitched
 * forward the head's own Y axis points down-range, so yawing the head rolls it
 * sideways like a confused puppy instead of sweeping the nose across the ground.
 * On the neck, with Three's XYZ order applying the yaw before the pitch, the
 * swing happens about the dog's own upright axis and then the whole assembly
 * tips down — which is the motion a dog actually makes.
 */
export const DOG_CAST = { sweep: 0.4, hertz: 0.8, neckDown: 1.15 } as const;

/** Leg swing. **The dog does not have a run** — both beats drive every gait off this. */
export const DOG_GAIT = { swing: 0.32, hertz: 8, tailSwing: 0.16, tailHertzScale: 0.7 } as const;

export interface DogPoseInput {
  /** Legs swing in diagonal pairs. True for any gait; there is only one. */
  readonly walking: boolean;
  /** The head is down on the ground, following or reading a line. */
  readonly noseDown: boolean;
  /**
   * The nose sweeps side to side, and it implies `noseDown`.
   *
   * **Only a casting dog sweeps.** A dog following a line it has already found
   * and sweeping its head at the same time reads as confusion rather than as
   * certainty, which is the wrong note for an animal these beats describe as
   * inevitable. Both copies of this driver already made that distinction; it is
   * the one thing they agreed about.
   */
  readonly casting: boolean;
  /** Whether the tail sways with the gait. Beat 4's dog is at heel and does not. */
  readonly tail: boolean;
  /** The render-step clock, in seconds. Never a fixed-step value. */
  readonly clock: number;
}

/** Write one dog's presentation pose. Pure in, rotations out; no state kept. */
export function applyDogPose(parts: DogParts, input: DogPoseInput): void {
  const swing = input.walking ? Math.sin(input.clock * DOG_GAIT.hertz) * DOG_GAIT.swing : 0;
  parts.legs.forEach((leg, index) => {
    // Diagonal pairs, which is what a walking quadruped does and what stops four
    // legs swinging as one object.
    leg.rotation.x = (index % 2 === 0 ? 1 : -1) * swing;
  });

  parts.neck.rotation.x = input.noseDown || input.casting ? DOG_CAST.neckDown : 0;
  parts.neck.rotation.y = input.casting
    ? Math.sin(input.clock * Math.PI * 2 * DOG_CAST.hertz) * DOG_CAST.sweep
    : 0;
  parts.tail.rotation.x = input.walking && input.tail
    ? Math.sin(input.clock * DOG_GAIT.hertz * DOG_GAIT.tailHertzScale) * DOG_GAIT.tailSwing
    : 0;
}
